import { ExcelFileViewer } from "@/components/common/ExcelViewer/ExcelFileViewer"
import { PdfViewer } from "@/components/common/PdfViewer/PdfViewer"
import { Button } from "@/components/ui/button"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2 } from "@tabler/icons-react"
import {
  ArrowLeft,
  Check,
  ExternalLink,
  FileText,
  Info,
  UserCheck,
} from "lucide-react"
import { Link } from "react-router"
import type {
  QuestionnaireEditable,
  QuestionnaireIntake,
} from "../../api/customerRequestsApi"
import { daysUntilLabel, dueDateTone } from "../../api/customerRequestsApi"
import { intlLocale } from "../../utils/date"
import { CustomerPicker } from "../shared/CustomerPicker"
import { EditableRow } from "../shared/EditableRow"
import { FooterActionBar } from "../shared/FooterActionBar"
import { McPill } from "../shared/McPill"
import { SectionLabel } from "../shared/SectionLabel"
import {
  RegulationKeysPicker,
  UnmappedFrameworkPills,
} from "./RequestRegulations"
import { customerRequestDetailPath } from "./routes"

const EXCEL_EXTENSIONS = new Set(["xlsx", "xls"])

const getFileExtension = (fileName: string | null | undefined): string => {
  if (!fileName) return ""
  const parts = fileName.split(".")
  return parts.length > 1 ? (parts.pop()?.toLowerCase() ?? "") : ""
}

/** An earlier upload of the same bytes, named on the confirm page so a
 *  re-upload is visible as one before it is stored. */
export interface DuplicateOf {
  reference: string
  id: string | null
  uploadedAt: string | null
}

interface ConfirmStepProps {
  intake: QuestionnaireIntake
  editable: QuestionnaireEditable
  setEditable: (e: QuestionnaireEditable) => void
  onBack: () => void
  onStore: () => void
  storing?: boolean
  storeError?: string | null
  duplicateOf?: DuplicateOf
}

export function ConfirmStep({
  intake,
  editable,
  setEditable,
  onBack,
  onStore,
  storing,
  storeError,
  duplicateOf,
}: ConfirmStepProps) {
  const { translate, currentLanguage } = useTranslation()
  const regulationSettingsOn = useFeatureFlag("MC_REGULATION_SETTINGS")

  const fileExtension = getFileExtension(intake.fileName ?? intake.doc)
  const isExcelFile = EXCEL_EXTENSIONS.has(fileExtension)

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 pt-6 pb-4">
        <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-grey4">
          <ResizablePanelGroup
            orientation="horizontal"
            autoSaveId="mc-q-confirm-split"
          >
            {/* LEFT — all record + detected fields */}
            <ResizablePanel
              id="records"
              defaultSize={55}
              minSize={35}
              className="flex min-h-0 flex-col bg-grey3"
            >
              {/* react-resizable-panels sets overflow:hidden as an inline
                  style on the Panel itself, which beats any overflow-auto
                  class — the scroll container has to be this inner div. */}
              <div className="h-full min-h-0 overflow-y-auto">
                <div className="flex flex-col gap-3 p-5">
                  <div className="flex items-center gap-2.5">
                    <SectionLabel>
                      {translate("MC_Q_RECORD_SECTION")}
                    </SectionLabel>
                    <McPill tone="attention">
                      {translate("MC_Q_RECORD_PILL")}
                    </McPill>
                  </div>
                </div>

                <div className="flex flex-col gap-4 px-5 pb-5">
                  {/* Customer — select or create */}
                  <div>
                    <div className="mb-1.5 text-xs text-grey5">
                      {translate("MC_Q_FIELD_CUSTOMER")}
                      <span className="ml-0.5 text-red1">*</span>
                    </div>
                    <CustomerPicker
                      customerId={editable.customerId}
                      customerName={editable.customer}
                      onChange={(id, name) =>
                        setEditable({
                          ...editable,
                          customerId: id,
                          customer: name,
                        })
                      }
                    />
                  </div>

                  {/* Due date with urgency indicator */}
                  <div>
                    <div className="mb-1.5 text-xs text-grey5">
                      {translate("MC_Q_FIELD_DUE")}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={editable.due}
                        onChange={(e) =>
                          setEditable({ ...editable, due: e.target.value })
                        }
                        className="font-mono h-8 rounded-md border border-grey4 bg-grey2 px-2.5 text-xs text-white outline-hidden focus:border-yellow1"
                      />
                      <span
                        className={`text-xs ${dueDateTone(editable.due) === "danger" ? "text-red1" : dueDateTone(editable.due) === "warning" ? "text-warning" : "text-grey5"}`}
                      >
                        {daysUntilLabel(editable.due, translate)}
                      </span>
                    </div>
                    {intake.dueSource === "extracted" && (
                      <div className="mt-1.5 flex items-start gap-1 text-xs text-grey5">
                        <Info size={11} className="mt-px shrink-0" />
                        {translate("MC_Q_DUE_DATE_EXTRACTED_HINT")}
                      </div>
                    )}
                  </div>

                  {/* Regulations: the reviewer confirms them as canonical keys while
                      regulation settings are on (TUR-1734); otherwise the detected
                      wording, read-only, as before. */}
                  <div>
                    <div className="mb-1.5 text-xs text-grey5">
                      {translate("MC_Q_FIELD_FRAMEWORK")}
                    </div>
                    {regulationSettingsOn ? (
                      <div className="flex flex-col gap-1.5">
                        <RegulationKeysPicker
                          value={editable.regulationKeys}
                          onChange={(regulationKeys) =>
                            setEditable({ ...editable, regulationKeys })
                          }
                        />
                        {intake.unmappedFrameworks.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <UnmappedFrameworkPills
                              values={intake.unmappedFrameworks}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {editable.frameworks.map((f) => (
                          <McPill key={f} tone="muted">
                            {f}
                          </McPill>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Article codes — removable chips */}
                  <div>
                    <div className="mb-1.5 text-xs text-grey5">
                      {translate("MC_Q_CODES_TITLE", {
                        count: editable.articleCodes.length,
                      })}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {editable.articleCodes.map((c) => (
                        <McPill key={c} tone="muted" className="font-mono">
                          {c}
                        </McPill>
                      ))}
                    </div>
                    <div className="mt-2 flex items-start gap-1 text-xs text-grey5">
                      <Info size={11} className="mt-px shrink-0" />
                      {translate("MC_Q_DETECTED_CODES_NOTE")}
                    </div>
                  </div>

                  {/* Notes */}
                  <EditableRow
                    label={translate("MC_Q_FIELD_NOTES")}
                    value={editable.notes}
                    multiline
                    onChange={(v) => setEditable({ ...editable, notes: v })}
                  />
                </div>
              </div>
            </ResizablePanel>

            <ResizableHandle />

            {/* RIGHT — original document preview */}
            <ResizablePanel
              id="document"
              defaultSize={45}
              minSize={30}
              className="flex min-h-0 flex-col bg-grey1"
            >
              <div className="flex h-[46px] shrink-0 items-center gap-2.5 border-b border-grey4 bg-grey2 px-4">
                <span className="inline-flex items-center gap-2 text-[12px] whitespace-nowrap text-grey5">
                  <FileText size={15} />
                  {translate("MC_Q_CONFIRM_PREVIEW_TITLE")}
                </span>
                <span className="h-[22px] w-px shrink-0 bg-grey4" />
                <span className="font-mono max-w-[240px] truncate text-[12px] text-grey5">
                  {intake.doc}
                </span>
                <span className="flex-1" />
                {intake.fileUrl && (
                  <a
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md text-grey5 hover:bg-grey3 hover:text-white"
                    href={intake.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink size={17} />
                  </a>
                )}
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                {intake.fileUrl ? (
                  isExcelFile ? (
                    <ExcelFileViewer file={intake.fileUrl} />
                  ) : (
                    <PdfViewer
                      fileUrl={intake.fileUrl}
                      showControls={false}
                      className="h-full"
                    />
                  )
                ) : (
                  <div className="flex h-full items-center justify-center px-6 text-center text-[13px] text-grey5">
                    {translate("MC_Q_CONFIRM_PREVIEW_EMPTY")}
                  </div>
                )}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>

        {/* Persistent, above the actions: six byte-identical PDFs went in over
            45 minutes without anyone noticing (F8/A1). It names the earlier
            request and links to it, so the reader can check before storing a
            second copy. Not a refusal, the same questionnaire can legitimately
            arrive twice. */}
        {duplicateOf && (
          <div className="shrink-0 rounded-md border border-yellow1/30 bg-yellow1/8 p-3 text-xs text-white">
            {translate("MC_Q_CONFIRM_DUPLICATE", {
              reference: duplicateOf.reference,
              date: duplicateOf.uploadedAt
                ? new Date(duplicateOf.uploadedAt).toLocaleDateString(
                    intlLocale(currentLanguage),
                  )
                : "",
            })}{" "}
            {duplicateOf.id && (
              <Link
                to={customerRequestDetailPath(duplicateOf.id)}
                className="font-medium text-yellow1 hover:underline"
              >
                {translate("MC_Q_CONFIRM_DUPLICATE_OPEN")}
              </Link>
            )}
          </div>
        )}

        {storeError && (
          <div className="shrink-0 rounded-md border border-red1/25 bg-red1/8 p-3 text-xs text-red1">
            {storeError}
          </div>
        )}
      </div>

      <FooterActionBar
        left={
          <>
            <UserCheck size={13} className="shrink-0 text-yellow1" />
            {translate("MC_Q_FOOTER_MSG")}
          </>
        }
      >
        <Button
          variant="outline"
          size="sm"
          className="border-grey4 text-white hover:bg-grey2"
          onClick={onBack}
          disabled={storing}
        >
          <ArrowLeft size={13} className="mr-1.5" />
          {translate("MC_Q_BTN_BACK_UPLOAD")}
        </Button>
        <Button
          size="sm"
          className="bg-yellow1 text-black hover:bg-yellow2"
          onClick={onStore}
          disabled={storing || !editable.customerId}
        >
          {storing ? (
            <IconLoader2 size={13} className="mr-1.5 animate-spin" />
          ) : (
            <Check size={13} className="mr-1.5" />
          )}
          {storing
            ? translate("MC_Q_BTN_STORING")
            : translate("MC_Q_BTN_STORE")}
        </Button>
      </FooterActionBar>
    </div>
  )
}
